import { useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useQuery } from 'urql';
import { z } from 'zod';
import { Q_CLIENTES } from '@/modules/administracion/clientes/clientes.gql.js';

const toNumber = value => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? undefined : value;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const nonNegativeNumber = message =>
  z.preprocess(
    toNumber,
    z
      .number({ invalid_type_error: message, required_error: message })
      .min(0, message),
  );

const nonNegativeInt = message =>
  z.preprocess(
    toNumber,
    z
      .number({ invalid_type_error: message, required_error: message })
      .min(0, message)
      .refine(value => Number.isInteger(value), message),
  );

const requiredText = message => z.coerce.string({ invalid_type_error: message, required_error: message }).trim().min(1, message);

export const remitoSchema = z.object({
  numero_remito: requiredText('Ingrese el número de remito'),
  bultos: nonNegativeInt('Ingrese la cantidad de bultos (entero)'),
  descripcion: requiredText('Ingrese la descripción del remito'),
  kg_reales: nonNegativeNumber('Ingrese los kilogramos reales'),
  m3: nonNegativeNumber('Ingrese los metros cúbicos'),
  kg_aforado: nonNegativeNumber('Ingrese el peso aforado'),
  valor_declarado: nonNegativeNumber('Ingrese el valor declarado'),
});

const guiaSchema = z
  .object({
    numero_guia: z.string().optional(),
    fecha_emision: z
      .string({ required_error: 'La fecha es obligatoria' })
      .min(1, 'La fecha es obligatoria'),
    hora_emision: z
      .string({ required_error: 'La hora es obligatoria' })
      .min(1, 'La hora es obligatoria'),
    usuario: z.string().min(1, 'El usuario es requerido'),
    estado: z.string().min(1, 'El estado es requerido'),
    sucursal_origen_id: requiredText('Seleccione la sucursal origen'),
    sucursal_destino_id: z.coerce.string().optional(),
    linea_numero: requiredText('Seleccione una línea'),
    modalidad_cobro: z.enum(['cta_cte_destino', 'cta_cte_origen', 'contado_origen', 'contado_destino'], {
      required_error: 'Seleccione la modalidad de cobro',
    }),
    tipo_entrega: z.enum(['domicilio', 'retiro_sucursal'], {
      required_error: 'Seleccione el tipo de entrega',
    }),
    remitente_id: requiredText('Seleccione un remitente'),
    destinatario_id: requiredText('Seleccione un destinatario'),
    cliente_pagador_id: z.string().optional(),
    domicilio_remitente: z.string().optional(),
    localidad_remitente: z.string().optional(),
    provincia_remitente: z.string().optional(),
    telefono_remitente: z.string().optional(),
    contacto_remitente: z.string().optional(),
    domicilio_destinatario: z.string().optional(),
    localidad_destinatario: z.string().optional(),
    provincia_destinatario: z.string().optional(),
    telefono_destinatario: z.string().optional(),
    contacto_destinatario: z.string().optional(),
    localidad_emision_id: z.string().optional(),
    localidad_destino_id: z.string().optional(),
    localidad_encargada_id: z.string().optional(),
    contrareembolso: nonNegativeNumber('Ingrese el contrarreembolso'),
    total_valorizado: nonNegativeNumber('Ingrese el total valorizado'),
    valorizada: z.boolean(),
    observaciones: z.string().optional(),
    remitos: z.array(remitoSchema).min(1, 'Debe agregar al menos un remito'),
  })
  .superRefine((data, ctx) => {
    // Validar números de remito únicos
    const seen = {};
    data.remitos.forEach((remito, index) => {
      if (seen[remito.numero_remito]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['remitos', index, 'numero_remito'],
          message: 'El número de remito ya fue agregado',
        });
      }
      seen[remito.numero_remito] = true;
    });
    
    // Validar que remitente ≠ destinatario
    if (data.remitente_id && data.destinatario_id && data.remitente_id === data.destinatario_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['destinatario_id'],
        message: 'El destinatario no puede ser el mismo que el remitente',
      });
    }
  });

const applyError = (target, path, value) => {
  if (!path.length) {
    return;
  }
  const [current, ...rest] = path;
  const key = typeof current === 'number' ? current : current;
  if (!rest.length) {
    target[key] = {
      type: value.type || 'validation',
      message: value.message,
    };
    return;
  }

  if (!target[key]) {
    target[key] = {};
  }
  applyError(target[key], rest, value);
};

const createResolver = schema => values => {
  const parsed = schema.safeParse(values);
  if (parsed.success) {
    return { values: parsed.data, errors: {} };
  }

  const formErrors = {};
  parsed.error.issues.forEach(issue => {
    applyError(formErrors, issue.path, {
      message: issue.message,
      type: issue.code,
    });
  });

  return { values: {}, errors: formErrors };
};

const formatDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = date => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const buildDefaultValues = () => {
  const now = new Date();
  return {
    numero_guia: '',
    fecha_emision: formatDate(now),
    hora_emision: formatTime(now),
    usuario: 'usuario.demo',
    estado: 'EMITIDA',
    sucursal_origen_id: '',
    sucursal_destino_id: '',
    linea_numero: '',
    remitente_id: '',
    destinatario_id: '',
    cliente_pagador_id: '',
    domicilio_remitente: '',
    localidad_remitente: '',
    provincia_remitente: '',
    telefono_remitente: '',
    contacto_remitente: '',
    domicilio_destinatario: '',
    localidad_destinatario: '',
    provincia_destinatario: '',
    telefono_destinatario: '',
    contacto_destinatario: '',
    localidad_emision_id: '',
    localidad_destino_id: '',
    localidad_encargada_id: '',
    modalidad_cobro: 'cta_cte_destino',
    tipo_entrega: 'domicilio',
    contrareembolso: 0,
    total_valorizado: 0,
    valorizada: false,
    observaciones: '',
    remitos: [],
  };
};

export function useGuiaForm() {
  // Fetch clientes desde GraphQL
  const [{ data: clientesData, fetching: fetchingClientes }] = useQuery({
    query: Q_CLIENTES,
    variables: {
      page: 1,
      pageSize: 1000, // Traer todos los clientes activos para el select
      filter: { activo: true },
    },
  });

  const form = useForm({
    mode: 'onChange',
    defaultValues: buildDefaultValues(),
    resolver: createResolver(guiaSchema),
  });

  const remitosArray = useFieldArray({
    control: form.control,
    name: 'remitos',
  });

  const sucursales = useMemo(
    () => [
      { value: '', label: 'Seleccione sucursal' },
      { value: 'CABA', label: 'CABA' },
      { value: 'ROS', label: 'Rosario' },
      { value: 'CBA', label: 'Córdoba' },
      { value: 'MDP', label: 'Mar del Plata' },
    ],
    [],
  );

  // Mapear clientes desde GraphQL a formato select
  const clientes = useMemo(() => {
    if (!clientesData?.clientes?.edges) {
      return [{ value: '', label: 'Seleccione cliente' }];
    }
    
    const clientesOptions = clientesData.clientes.edges.map(edge => ({
      value: edge.node.id,
      label: edge.node.razonSocial || edge.node.nombreFantasia || `Cliente ${edge.node.id}`,
      cuit: edge.node.cuit,
      direccion: edge.node.direccion,
    }));
    
    return [
      { value: '', label: 'Seleccione cliente' },
      ...clientesOptions,
    ];
  }, [clientesData]);

  const clientesRecords = useMemo(
    () => (clientesData?.clientes?.edges || []).map(edge => edge.node),
    [clientesData],
  );

  const modalidadOptions = useMemo(
    () => [
      { value: 'cta_cte_destino', label: 'Cuenta Corriente Destino' },
      { value: 'cta_cte_origen', label: 'Cuenta Corriente Origen' },
      { value: 'contado_origen', label: 'Contado Origen' },
      { value: 'contado_destino', label: 'Contado Destino' },
    ],
    [],
  );

  const entregaOptions = useMemo(
    () => [
      { value: 'domicilio', label: 'Envío a domicilio' },
      { value: 'retiro_sucursal', label: 'Retiro en sucursal' },
    ],
    [],
  );

  const addRemito = remito => {
    const parsed = remitoSchema.safeParse(remito);
    if (!parsed.success) {
      const { fieldErrors } = parsed.error.flatten();
      const errors = Object.keys(fieldErrors).reduce((acc, key) => {
        if (fieldErrors[key] && fieldErrors[key].length) {
          acc[key] = fieldErrors[key][0];
        }
        return acc;
      }, {});
      return { ok: false, errors };
    }

    const current = form.getValues('remitos');
    if (current.some(item => item.numero_remito === parsed.data.numero_remito)) {
      return {
        ok: false,
        errors: { numero_remito: 'Este remito ya fue agregado' },
      };
    }

    remitosArray.append(parsed.data);
    return { ok: true };
  };

  const removeRemito = index => remitosArray.remove(index);

  const updateRemito = (index, remito) => {
    const parsed = remitoSchema.safeParse(remito);
    if (!parsed.success) {
      const { fieldErrors } = parsed.error.flatten();
      const errors = Object.keys(fieldErrors).reduce((acc, key) => {
        if (fieldErrors[key] && fieldErrors[key].length) {
          acc[key] = fieldErrors[key][0];
        }
        return acc;
      }, {});
      return { ok: false, errors };
    }

    const current = form.getValues('remitos');
    if (current.some((item, itemIndex) => itemIndex !== index && item.numero_remito === parsed.data.numero_remito)) {
      return {
        ok: false,
        errors: { numero_remito: 'Este remito ya fue agregado' },
      };
    }

    remitosArray.update(index, parsed.data);
    return { ok: true };
  };

  const totalBultos = useMemo(() => {
    return remitosArray.fields.reduce((acc, current) => acc + Number(current.bultos || 0), 0);
  }, [remitosArray.fields]);

  const valorDeclaradoTotal = useMemo(() => {
    return remitosArray.fields.reduce((acc, current) => acc + Number(current.valor_declarado || 0), 0);
  }, [remitosArray.fields]);

  const totalKgReales = useMemo(() => {
    return remitosArray.fields.reduce((acc, current) => acc + Number(current.kg_reales || 0), 0);
  }, [remitosArray.fields]);

  const totalM3 = useMemo(() => {
    return remitosArray.fields.reduce((acc, current) => acc + Number(current.m3 || 0), 0);
  }, [remitosArray.fields]);

  return {
    form,
    remitosArray,
    addRemito,
    updateRemito,
    removeRemito,
    sucursales,
    clientes,
    clientesRecords,
    fetchingClientes,
    modalidadOptions,
    entregaOptions,
    totalBultos,
    totalKgReales,
    totalM3,
    valorDeclaradoTotal,
    resetForm: () => form.reset(buildDefaultValues()),
  };
}

export default useGuiaForm;
